import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../../utils/helpers';
import BankList from '../partials/BankList';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const ConnectBankModal = ({ show, onHide, onAccountConnected }) => {
  const [accountNumber, setAccountNumber] = useState('');
  const [selectedBank, setSelectedBank] = useState({ code: '', name: '' });
  const [accountName, setAccountName] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setAccountNumber('');
      setSelectedBank({ code: '', name: '' });
      setAccountName('');
    }
  }, [show]);

  // Account resolution with debouncing
  const resolveAccount = useCallback(async () => {
    if (!selectedBank.code || accountNumber.length !== 10) return;

    setIsResolving(true);
    try {
      const res = await api.get(API_CONFIG.PAYMENTS.RESOLVE_ACCOUNT, {
        params: {
          account_number: accountNumber,
          bank_code: selectedBank.code
        },
        timeout: 10000
      });

      const { account_name } = res.data;
      setAccountName(account_name);
    } catch (err) {
      setAccountName('');
      const errorMessage = err.response?.data?.message || 'Invalid account details';
      showToast(errorMessage, 'error');
    } finally {
      setIsResolving(false);
    }
  }, [selectedBank.code, accountNumber]);

  // Debounced account resolution
  useEffect(() => {
    if (accountNumber.length === 10 && selectedBank.code) {
      const timer = setTimeout(() => {
        resolveAccount();
      }, 800);
      return () => clearTimeout(timer);
    } else {
      setAccountName('');
    }
  }, [accountNumber, selectedBank, resolveAccount]);

  const handleBankSelect = (code, name) => {
    setSelectedBank({ code, name });
  };

  // Form validation
  const validateForm = () => {
    if (!accountNumber || !selectedBank.code || !accountName) {
      showToast('Please provide valid account details', 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        account_number: accountNumber,
        bank_code: selectedBank.code,
        bankName: selectedBank.name,
        accountName
      };

      const response = await api.post(API_CONFIG.PAYMENTS.CONNECT_BANK_ACCOUNT, payload);

      if (response.data.success) {
        showToast(response.data.message || 'Bank account connected successfully!', 'success');

        if (onAccountConnected) {
          onAccountConnected(response.data.data);
        }

        onHide();
      } else {
        throw new Error(response.data.error || 'Failed to connect bank account');
      }
    } catch (error) {
      console.error('Connect bank account error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to connect bank account';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50">
            <h5 className="text-xl font-semibold">Connect Bank Account</h5>
            <button
              onClick={onHide}
              disabled={isSubmitting}
              className="text-gray-500 hover:text-gray-700 transition p-1 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Bank Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank
                </label>
                <BankList
                  onBankSelect={handleBankSelect}
                  selectedBankCode={selectedBank.code}
                  disabled={isSubmitting}
                />
              </div>

              {/* Account Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                    maxLength={10}
                    required
                    disabled={isSubmitting}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="10-digit account number"
                  />
                  {isResolving && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Account Name */}
              {accountName && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-700 font-medium text-sm">
                    Account Name: {accountName}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={
                  (!accountNumber || !selectedBank.code || !accountName) ||
                  isSubmitting
                }
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3.5 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  'Connect Account'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConnectBankModal;
